// Admin v2 — Irányítópult (Figma ADM_01). Élő adatok: users, venues, bookings, sessions.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getCountFromServer, getDocs, limit, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Icon } from './icons';
import { Avatar, Card, IconTile, Modal, PageHeader, SectionTitle, Segmented, Spinner, StatCard, Tone, Button } from './ui';
import { AdminUser, fmtInt, normalizeUser, pctDelta, timeAgo, toDate } from './data';

type Range = 'week' | 'month' | 'year';
interface Booking { at: Date; revenue: number }
interface Activity { id: string; icon: string; tone: Tone; title: string; sub: string; at: Date }

const REVENUE_KEYS = ['totalPrice', 'price', 'amount', 'totalAmount'];
const bookingRevenue = (v: Record<string, unknown>) => {
  for (const k of REVENUE_KEYS) if (typeof v[k] === 'number') return v[k] as number;
  return 0;
};

const DAY = 864e5;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

function buckets(range: Range, list: Booking[]) {
  const now = new Date();
  const out: { label: string; count: number; revenue: number }[] = [];
  if (range === 'year') {
    for (let i = 11; i >= 0; i--) {
      const m = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const next = new Date(m.getFullYear(), m.getMonth() + 1, 1);
      const inM = list.filter((b) => b.at >= m && b.at < next);
      out.push({ label: m.toLocaleString('hu-HU', { month: 'short' }), count: inM.length, revenue: inM.reduce((s, b) => s + b.revenue, 0) });
    }
  } else {
    const days = range === 'week' ? 7 : 30;
    const today = startOfDay(now);
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today.getTime() - i * DAY);
      const next = new Date(d.getTime() + DAY);
      const inD = list.filter((b) => b.at >= d && b.at < next);
      out.push({ label: `${d.getMonth() + 1}.${d.getDate()}.`, count: inD.length, revenue: inD.reduce((s, b) => s + b.revenue, 0) });
    }
  }
  return out;
}

function AreaChart({ points, unit }: { points: { label: string; value: number }[]; unit: string }) {
  const W = 680, H = 240;
  const max = Math.max(1, ...points.map((p) => p.value));
  const step = points.length > 1 ? W / (points.length - 1) : W;
  const xy = points.map((p, i) => [i * step, H - 8 - (p.value / max) * (H - 24)] as const);
  const line = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${W},${H} L0,${H} Z`;
  const [hover, setHover] = useState<number | null>(null);
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[240px]" preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const i = Math.round(((e.clientX - r.left) / r.width) * (points.length - 1));
          setHover(Math.max(0, Math.min(points.length - 1, i)));
        }}
      >
        <defs>
          <linearGradient id="dqArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00AFF2" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#00AFF2" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3].map((i) => (
          <line key={i} x1="0" x2={W} y1={(H / 3) * i + 0.5} y2={(H / 3) * i + 0.5} stroke="#31383D" strokeOpacity="0.6" vectorEffect="non-scaling-stroke" />
        ))}
        <path d={area} fill="url(#dqArea)" />
        <path d={line} fill="none" stroke="#00AFF2" strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        {hover !== null && (
          <line x1={xy[hover][0]} x2={xy[hover][0]} y1="0" y2={H} stroke="#00AFF2" strokeOpacity="0.4" vectorEffect="non-scaling-stroke" />
        )}
      </svg>
      {hover !== null && (
        <div
          className="absolute top-2 px-3 py-1.5 rounded-lg bg-[#1d232a] border border-[#31383D] text-[12px] text-white pointer-events-none whitespace-nowrap"
          style={{ left: `clamp(0px, calc(${(hover / Math.max(1, points.length - 1)) * 100}% - 50px), calc(100% - 110px))` }}
        >
          <span className="text-[#8a8f99]">{points[hover].label}</span> · {fmtInt(points[hover].value)} {unit}
        </div>
      )}
    </div>
  );
}

const QUICK = [
  { to: '/users', icon: 'Users', title: 'Felhasználó kezelés', sub: 'Fiókok és jogosultságok' },
  { to: '/venues', icon: 'MapPin', title: 'Helyszín kezelés', sub: 'Helyszínek és üzemeltetők' },
  { to: '/statistics', icon: 'ChartBar', title: 'Helyszín statisztikák', sub: 'Teljesítmény és analitika' },
  { to: '/reports', icon: 'FileText', title: 'Bevételi riportok', sub: 'Generálás és exportálás' },
  { to: '/settings', icon: 'Gear', title: 'Rendszerbeállítások', sub: 'Rendszerszintű beállítások' },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [venues, setVenues] = useState<{ id: string; name: string; city: string; at: Date | null }[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [activeSessions, setActiveSessions] = useState<number | null>(null);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [range, setRange] = useState<Range>('month');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const yearAgo = Timestamp.fromDate(new Date(Date.now() - 400 * DAY));
      const safe = <T,>(p: Promise<T>, fb: T) => p.catch((e) => { console.warn('[dashboard]', e); return fb; });
      const [uSnap, vSnap, bApp, bWeb, sCount, sRecent] = await Promise.all([
        safe(getDocs(collection(db, 'users')), null),
        safe(getDocs(collection(db, 'venues')), null),
        safe(getDocs(query(collection(db, 'bookings'), where('startAt', '>=', yearAgo))), null),
        safe(getDocs(query(collection(db, 'bookings'), where('startTime', '>=', yearAgo))), null),
        safe(getCountFromServer(query(collection(db, 'sessions'), where('status', '==', 'active'))), null),
        safe(getDocs(query(collection(db, 'sessions'), orderBy('startTime', 'desc'), limit(15))), null),
      ]);
      if (!alive) return;

      const us = uSnap ? uSnap.docs.map((d) => normalizeUser(d.id, d.data())) : [];
      const vs = vSnap ? vSnap.docs.map((d) => {
        const v = d.data();
        return { id: d.id, name: String(v.name ?? 'Helyszín'), city: String(v.city ?? v.address?.city ?? ''), at: toDate(v.createdAt) };
      }) : [];
      const bs: Booking[] = [];
      const acts: Activity[] = [];
      const seen = new Set<string>();
      for (const snap of [bApp, bWeb]) {
        snap?.docs.forEach((d) => {
          if (seen.has(d.id)) return;
          seen.add(d.id);
          const v = d.data();
          const st = String(v.state ?? v.status ?? '');
          if (st === 'cancelled' || st === 'canceled' || st === 'rejected') return;
          const at = toDate(v.startAt ?? v.startTime);
          if (!at) return;
          bs.push({ at, revenue: bookingRevenue(v) });
          const created = toDate(v.createdAt);
          if (created) {
            acts.push({
              id: `b-${d.id}`, icon: 'CalendarBlank', tone: 'cyan', title: 'Új foglalás',
              sub: [v.clubName ?? v.venueName, v.board ? `${v.board}. tábla` : ''].filter(Boolean).join(' · '), at: created,
            });
          }
        });
      }
      us.forEach((u) => u.createdAt && acts.push({
        id: `u-${u.uid}`, icon: 'UserCheck', tone: 'cyan', title: 'Új felhasználó regisztrált',
        sub: [u.displayName, u.username && `@${u.username}`].filter(Boolean).join(' · '), at: u.createdAt,
      }));
      vs.forEach((v) => v.at && acts.push({
        id: `v-${v.id}`, icon: 'MapPin', tone: 'gold', title: 'Helyszín hozzáadva', sub: [v.name, v.city].filter(Boolean).join(' · '), at: v.at,
      }));
      sRecent?.docs.forEach((d) => {
        const v = d.data();
        const at = toDate(v.endTime ?? v.startTime);
        if (!at) return;
        const done = v.status !== 'active';
        acts.push({
          id: `s-${d.id}`, icon: done ? 'Check' : 'PlayCircle', tone: 'green',
          title: done ? 'Munkamenet befejezve' : 'Munkamenet indítva',
          sub: [v.venueName, done ? `${v.matchCount ?? 0} mérkőzés` : v.boardName ?? (v.boardNumber ? `${v.boardNumber}. tábla` : '')].filter(Boolean).join(' · '),
          at,
        });
      });
      acts.sort((a, b) => b.at.getTime() - a.at.getTime());

      setUsers(us);
      setVenues(vs);
      setBookings(bs);
      setActiveSessions(sCount ? sCount.data().count : null);
      setActivity(acts.slice(0, 40));
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const me = users.find((u) => u.uid === user?.uid);
  const greetName = (me?.username || me?.displayName || user?.displayName || '').toUpperCase();

  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const before = users.filter((u) => !u.createdAt || u.createdAt < monthStart).length;
    const newVenues = venues.filter((v) => v.at && v.at >= monthStart).length;
    const revThis = bookings.filter((b) => b.at >= monthStart && b.at <= now).reduce((s, b) => s + b.revenue, 0);
    const revPrev = bookings.filter((b) => b.at >= prevStart && b.at < monthStart).reduce((s, b) => s + b.revenue, 0);
    const userDelta = before > 0 ? pctDelta(users.length, before) : null;
    return {
      userDelta,
      newVenues,
      revThis,
      revDelta: revPrev > 0 ? pctDelta(revThis, revPrev) : null,
    };
  }, [users, venues, bookings]);

  const series = useMemo(() => {
    const b = buckets(range, bookings);
    const hasRevenue = b.some((x) => x.revenue > 0);
    return {
      unit: hasRevenue ? 'Ft' : 'foglalás',
      points: b.map((x) => ({ label: x.label, value: hasRevenue ? x.revenue : x.count })),
    };
  }, [range, bookings]);

  const top = useMemo(
    () => [...users].filter((u) => u.dqScore > 0).sort((a, b) => b.dqScore - a.dqScore).slice(0, 4),
    [users],
  );

  const downloadReport = () => {
    const rows = [
      ['Mutató', 'Érték'],
      ['Összes felhasználó', String(users.length)],
      ['Helyszínek', String(venues.length)],
      ['Bevétel (aktuális hónap, Ft)', String(stats.revThis)],
      ['Aktív munkamenetek', String(activeSessions ?? '')],
      [],
      ['Időszak', series.unit === 'Ft' ? 'Bevétel (Ft)' : 'Foglalások'],
      ...series.points.map((p) => [p.label, String(p.value)]),
    ];
    const csv = '﻿' + rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `dartiqon-riport-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading) return <Spinner />;

  const ActivityRow = ({ a, last }: { a: Activity; last: boolean }) => (
    <div className={`flex items-center gap-3 py-3 ${last ? '' : 'border-b border-[#31383D]'}`}>
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0" style={{ background: `${a.tone === 'gold' ? '#d9a514' : a.tone === 'green' ? '#2bc48a' : '#00AFF2'}24`, color: a.tone === 'gold' ? '#d9a514' : a.tone === 'green' ? '#2bc48a' : '#00AFF2' }}>
        <Icon name={a.icon} size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-white truncate">{a.title}</div>
        <div className="text-[12px] text-[#8a8f99] truncate">{a.sub || '—'}</div>
      </div>
      <div className="text-[12px] text-[#8a8f99] whitespace-nowrap">{timeAgo(a.at)}</div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title={`Üdvözöljük újra${greetName ? `, ${greetName}` : ''}!`}
        sub="Ön rendszergazdai hozzáféréssel rendelkezik. Itt van, ami ma történik."
        right={<Button kind="ghost" icon="DownloadSimple" onClick={downloadReport}>Riport letöltése</Button>}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon="Users" tone="cyan" label="Összes felhasználó" value={fmtInt(users.length)} delta={stats.userDelta} />
        <StatCard icon="MapPin" tone="gold" label="Helyszínek" value={fmtInt(venues.length)}
          delta={stats.newVenues > 0 ? { text: `${stats.newVenues} új ebben a hónapban`, up: true } : null} />
        <StatCard icon="Coins" tone="green" label="Bevétel" value={`${fmtInt(stats.revThis)} Ft`} delta={stats.revDelta} />
        <StatCard icon="Clock" tone="purple" label="Aktív munkamenetek" value={activeSessions === null ? '—' : fmtInt(activeSessions)} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 mt-6">
        <Card className="!p-5">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h2 className="text-[15px] font-extrabold italic uppercase text-white">Foglalások és bevétel</h2>
            <Segmented value={range} onChange={setRange}
              options={[{ value: 'week', label: 'Hét' }, { value: 'month', label: 'Hónap' }, { value: 'year', label: 'Év' }]} />
          </div>
          <div className="border-t border-[#31383D]/60 pt-2">
            <AreaChart points={series.points} unit={series.unit} />
          </div>
          <div className="mt-2 text-[12px] text-[#8a8f99]">
            {series.unit === 'Ft' ? 'Bevétel forintban' : 'Foglalások száma'} · {range === 'week' ? 'utolsó 7 nap' : range === 'month' ? 'utolsó 30 nap' : 'utolsó 12 hónap'}
          </div>
        </Card>

        <Card className="!p-5">
          <SectionTitle>Admin panel</SectionTitle>
          <div className="flex flex-col gap-2.5">
            {QUICK.map((q) => (
              <Link key={q.to} to={q.to} className="flex items-center gap-3 p-3 rounded-xl bg-[#1d232a] hover:bg-[#232a32] transition">
                <IconTile icon={q.icon} tone="cyan" iconSize={18} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold text-white">{q.title}</span>
                  <span className="block text-[12px] text-[#8a8f99]">{q.sub}</span>
                </span>
                <Icon name="CaretRight" size={16} className="text-[#8a8f99]" />
              </Link>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 mt-6">
        <Card className="!p-5">
          <SectionTitle right={
            <button type="button" onClick={() => setShowAll(true)} className="inline-flex items-center gap-1 text-[13px] font-medium text-[#00AFF2] hover:underline">
              Összes tevékenység <Icon name="ArrowRight" size={14} />
            </button>
          }>Legutóbbi tevékenység</SectionTitle>
          {activity.length === 0
            ? <div className="py-6 text-[13px] text-[#8a8f99]">Még nincs tevékenység.</div>
            : activity.slice(0, 4).map((a, i, arr) => <ActivityRow key={a.id} a={a} last={i === arr.length - 1} />)}
        </Card>

        <Card className="!p-5">
          <SectionTitle right={
            <Link to="/leaderboard" className="inline-flex items-center gap-1 text-[13px] font-medium text-[#00AFF2] hover:underline">
              Teljes lista <Icon name="ArrowRight" size={14} />
            </Link>
          }>Top játékosok</SectionTitle>
          {top.length === 0 && <div className="py-6 text-[13px] text-[#8a8f99]">Még nincs CP-vel rendelkező játékos.</div>}
          <div className="flex flex-col">
            {top.map((p, i) => (
              <button key={p.uid} type="button" onClick={() => nav(`/users/${p.uid}`)}
                className="flex items-center gap-3 h-12 text-left rounded-lg hover:bg-white/5 px-1 transition">
                <span className={`w-4 text-[14px] font-semibold ${i === 0 ? 'text-[#d9a514]' : 'text-[#8a8f99]'}`}>{i + 1}</span>
                <Avatar src={p.avatarUrl} name={p.displayName} size={30} />
                <span className="flex-1 min-w-0 text-[13px] text-white truncate">{p.username || p.displayName}</span>
                <span className="text-[14px] font-bold text-[#2bc48a]">{fmtInt(p.dqScore)} CP</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {showAll && (
        <Modal title="Összes tevékenység" onClose={() => setShowAll(false)} width={620}>
          {activity.map((a, i, arr) => <ActivityRow key={a.id} a={a} last={i === arr.length - 1} />)}
        </Modal>
      )}
    </div>
  );
}
